import mitt, { Emitter } from 'mitt';
import { buildApiUrl } from '../../utils/formatHelpers';
import { useUserStore } from '../../stores/userStore';
import { getLogger, isAreaEnabled } from '../../utils/logger';
import {
  ConnectionStatus,
  SocketMessage,
  SocketEvent,
  LabSubscriptionMessage,
  LabSubscriptionResponse,
  TypedLabEvent,
  LabPartnerRegisterMessage,
  LabPartnerAskMessage,
  LabPartnerStreamMessageSchema,
} from './types';
import { streamProcessor } from './streamProcessor';

const logger = getLogger('socket');

type Events = {
  status: SocketEvent;
  subscription: SocketEvent;
  labEvent: SocketEvent;
  error: SocketEvent;
  labPartnerStream: SocketEvent;
  eject: SocketEvent;
};

class SocketConnection {
  private static instance: SocketConnection;
  private emitter: Emitter<Events>;
  private socket: WebSocket | null = null;
  private status: ConnectionStatus = 'disconnected';
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isReconnecting = false;

  // Circuit breaker pattern
  private failureCount = 0;
  private circuitBreakerThreshold = 5;
  private circuitBreakerTimeout = 60000; // 60 seconds
  private circuitBreakerResetTime: number | null = null;
  private isCircuitOpen = false;
  private keepAliveInterval: NodeJS.Timeout | null = null;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private wsUrl: string;
  private subscribedLabId: string | null = null;

  // Connection mutex to prevent race conditions
  private connectionPromise: Promise<void> | null = null;
  private isDisconnecting = false;

  // Subscription confirmation tracking
  private pendingSubscription: {
    resolve: () => void;
    reject: (reason: Error) => void;
    timeout: NodeJS.Timeout;
  } | null = null;
  private subscriptionRetryController: AbortController | null = null;
  private lastSubscriptionConfirmedAt: number | null = null;

  // Ping/pong health tracking
  private pingTimeout: NodeJS.Timeout | null = null;
  private lastPongReceived: number = Date.now();
  private missedPongs = 0;
  private readonly MAX_MISSED_PONGS = 2;
  private readonly PING_INTERVAL = 30000; // 30 seconds
  private readonly PONG_TIMEOUT = 5000; // 5 seconds to wait for pong

  // Network and visibility monitoring
  private isOnline = true;
  private isPageVisible = true;
  private networkListener: (() => void) | null = null;
  private offlineListener: (() => void) | null = null;
  private visibilityListener: (() => void) | null = null;

  private constructor() {
    this.emitter = mitt<Events>();
    // Socket URL will be set when connect is called
    this.wsUrl = '';

    // Set up network and visibility monitoring
    this.setupNetworkMonitoring();
    this.setupVisibilityMonitoring();
  }

  static getInstance(): SocketConnection {
    if (!SocketConnection.instance) {
      SocketConnection.instance = new SocketConnection();
    }
    return SocketConnection.instance;
  }

  async connect(): Promise<void> {
    // Check circuit breaker
    if (this.isCircuitOpen) {
      const now = Date.now();
      if (this.circuitBreakerResetTime && now < this.circuitBreakerResetTime) {
        logger.warn(`Circuit breaker open, waiting ${Math.ceil((this.circuitBreakerResetTime - now) / 1000)}s before retry`);
        return;
      } else {
        // Circuit breaker timeout expired, attempt half-open state
        logger.info('Circuit breaker timeout expired, attempting connection');
        this.isCircuitOpen = false;
        this.circuitBreakerResetTime = null;
      }
    }

    // Return existing connection attempt if in progress
    if (this.connectionPromise) {
      logger.debug('Connection already in progress, returning existing promise');
      return this.connectionPromise;
    }

    // Already connected
    if (this.status === 'connected') {
      logger.debug('Already connected', { timestamp: Date.now() });
      return;
    }

    // Don't interrupt ongoing disconnect
    if (this.isDisconnecting) {
      logger.debug('Disconnect in progress, waiting before connecting');
      return;
    }

    // Don't connect if offline
    if (!this.isOnline) {
      logger.info('Cannot connect while offline');
      return;
    }

    // Allow connection even if page is hidden to prevent message loss
    // WebSocket will remain open in background and receive messages
    if (!this.isPageVisible) {
      logger.debug('Page hidden, but connecting to prevent message loss');
    }

    // Create connection promise to prevent concurrent attempts
    this.connectionPromise = this.performConnection();

    try {
      await this.connectionPromise;
    } finally {
      this.connectionPromise = null;
    }
  }

  private async performConnection(): Promise<void> {
    logger.debug('Starting connection', {
      timestamp: Date.now(),
      previousStatus: this.status,
    });

    this.setStatus('connecting');

    try {
      // Get fresh token - this will automatically refresh if expired
      const getValidToken = useUserStore.getState().getValidToken;
      const token = await getValidToken();

      if (!token) {
        logger.error('No valid token available - user may need to re-authenticate');
        this.setStatus('error');
        this.emitter.emit('error', {
          type: 'error',
          data: { message: 'Authentication token unavailable' },
        });
        return;
      }

      // Verify we're still online and visible before proceeding
      if (!this.isOnline) {
        logger.info('Network went offline during token refresh');
        this.setStatus('disconnected');
        return;
      }

      // Build WebSocket URL (isShared=true to use shared endpoints for developer stages)
      this.wsUrl = buildApiUrl('socket', 'wss', '', true);
      logger.info('Connecting to:', this.wsUrl);

      // Close existing socket if any (should be rare due to mutex)
      if (this.socket) {
        this.detachSocketHandlers();
        this.socket.close();
        this.socket = null;
      }

      // Create new WebSocket with token in protocols
      const protocols = ['authorization', `token.${token}`];
      this.socket = new WebSocket(this.wsUrl, protocols);

      this.setupSocketHandlers();
    } catch (error) {
      logger.error('Failed to connect:', error);
      this.setStatus('error');
      this.scheduleReconnect();
    }
  }

  async reconnect(): Promise<void> {
    logger.info('Manual reconnect requested');

    // Wait for any ongoing connection to complete
    if (this.connectionPromise) {
      logger.debug('Waiting for ongoing connection to complete');
      await this.connectionPromise;
    }

    // Reset reconnection state to allow manual reconnection
    this.reconnectAttempts = 0;
    this.isReconnecting = false;

    // Reset circuit breaker on manual reconnect
    this.failureCount = 0;
    this.isCircuitOpen = false;
    this.circuitBreakerResetTime = null;

    // Stop timers before closing socket
    this.stopKeepAlive();
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    // Clear any existing socket - detach handlers first to prevent cascading reconnects
    if (this.socket) {
      this.detachSocketHandlers();
      this.socket.close();
      this.socket = null;
    }

    // Set status to disconnected to allow connect() to proceed
    this.setStatus('disconnected');

    // Attempt connection
    await this.connect();
  }

  async reconnectForLabSwitch(newLabId: string): Promise<void> {
    logger.info('🔄 Reconnecting for lab switch:', {
      previousLabId: this.subscribedLabId,
      newLabId,
    });

    // Cancel any in-progress subscription retry for old lab
    this.cancelSubscriptionRetry();

    // Clear the current lab subscription
    this.subscribedLabId = null;

    // Stop timers
    this.stopKeepAlive();
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    // Close socket cleanly (code 1000 = normal closure, won't trigger auto-reconnect)
    if (this.socket) {
      this.detachSocketHandlers();
      this.socket.close(1000, 'Lab switch');
      this.socket = null;
    }

    // Reset reconnection state
    this.reconnectAttempts = 0;
    this.isReconnecting = false;

    // Set status to disconnected
    this.setStatus('disconnected');

    // Reconnect with new lab
    await this.connect();

    // Subscribe to new lab (resubscribeToLab handles confirmation + retry)
    this.subscribedLabId = newLabId;
    if (this.status === 'connected') {
      this.resubscribeToLab();
    }
  }

  private detachSocketHandlers() {
    if (!this.socket) return;
    this.socket.onopen = null;
    this.socket.onmessage = null;
    this.socket.onclose = null;
    this.socket.onerror = null;
  }

  private setupSocketHandlers() {
    if (!this.socket) return;

    this.socket.onopen = () => {
      logger.info('Connected successfully');
      this.setStatus('connected');
      this.reconnectAttempts = 0;
      this.isReconnecting = false;

      // Reset circuit breaker on successful connection
      this.failureCount = 0;
      this.isCircuitOpen = false;
      this.circuitBreakerResetTime = null;

      // Re-subscribe to lab if we had one
      this.resubscribeToLab();

      // Start keep-alive
      this.startKeepAlive();
    };

    this.socket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        if (isAreaEnabled('socket')) {
          logger.debug('[Socket] Raw message received:', message.type || message.action || 'no-type', message);
        }
        this.handleMessage(message);
      } catch (error) {
        logger.error('Failed to parse message:', error);
      }
    };

    this.socket.onclose = (event) => {
      logger.info('Connection closed', {
        code: event.code,
        reason: event.reason,
      });
      this.setStatus('disconnected');
      this.stopKeepAlive();
      this.cancelSubscriptionRetry();

      // Only reconnect if not a normal closure
      if (event.code !== 1000) {
        this.scheduleReconnect();
      }
    };

    this.socket.onerror = (error) => {
      logger.error('WebSocket error:', error);
      this.setStatus('error');

      // Increment failure count for circuit breaker
      this.failureCount++;
      if (this.failureCount >= this.circuitBreakerThreshold) {
        logger.error(`Circuit breaker triggered after ${this.failureCount} failures`);
        this.isCircuitOpen = true;
        this.circuitBreakerResetTime = Date.now() + this.circuitBreakerTimeout;
        this.emitter.emit('error', {
          type: 'error',
          data: { message: 'Connection failures exceeded threshold, backing off' },
        });
      }
    };
  }

  private handleMessage(message: Record<string, unknown>) {
    // Any incoming message proves the connection is alive
    if (this.pingTimeout) {
      clearTimeout(this.pingTimeout);
      this.pingTimeout = null;
    }
    this.missedPongs = 0;
    this.lastPongReceived = Date.now();

    // Handle ping/pong - server sends {data: "pong"}
    if (message.data === 'pong' || message.action === 'pong') {

      if (isAreaEnabled('socket')) {
        logger.debug('[Socket] Pong received, connection healthy');
      }
      return;
    }

    // Handle eject (user disabled/banned by admin)
    if (message.action === 'eject') {
      logger.warn('[Socket] Received eject message:', message.data);
      this.emitter.emit('eject', {
        type: 'eject',
        data: message.data,
      } as SocketEvent);
      return;
    }

    // Handle echo responses
    if (message.action === 'echo') {
      if (isAreaEnabled('socket')) {
        logger.debug('[Socket] Echo response received');
      }
      return;
    }

    // Handle lab partner registration response
    if (message.action === 'lab-partner/register' && message.data === 'OK') {
      if (isAreaEnabled('socket')) {
        logger.debug('[Socket] Lab Partner session registered successfully');
      }
      return;
    }

    // Check if it's a lab partner registration response without action field
    if (message.data === 'OK' && !message.action && !message.type) {
      // Likely a registration response, ignore silently
      return;
    }

    // Handle lab partner stream messages
    if (this.isLabPartnerStreamMessage(message)) {
      try {
        // Validate the message with Zod schema
        const validatedMessage = LabPartnerStreamMessageSchema.parse(message);

        // Process through stream processor
        streamProcessor.processStreamMessage(validatedMessage);

        // Also emit raw event for debugging/monitoring
        const event: SocketEvent = {
          type: 'lab_partner_stream',
          data: validatedMessage,
        };
        this.emitter.emit('labPartnerStream', event);
      } catch (error) {
        logger.error('[Socket] Invalid lab partner stream message:', error);
      }
      return;
    }

    // Handle lab subscription confirmation
    if (this.isSubscriptionResponse(message)) {
      if (isAreaEnabled('socket')) {
        logger.debug('[Socket] Lab subscription confirmed:', message.subscribed);
      }

      // Record when confirmation arrived (used for late-arrival detection)
      this.lastSubscriptionConfirmedAt = Date.now();

      // Resolve pending subscription confirmation if waiting
      if (this.pendingSubscription) {
        clearTimeout(this.pendingSubscription.timeout);
        const { resolve } = this.pendingSubscription;
        this.pendingSubscription = null;
        resolve();
      }

      const event: SocketEvent = {
        type: 'subscription_confirmed',
        data: message,
      };
      this.emitter.emit('subscription', event);
      return;
    }

    // Handle lab events
    if (message.type && this.isLabEvent(message)) {
      const event: SocketEvent = {
        type: 'lab_event',
        data: message,
      };
      this.emitter.emit('labEvent', event);
      return;
    }

    // Handle errors
    if (message.error) {
      logger.error('[Socket] Error message:', message.error);
      const event: SocketEvent = {
        type: 'error',
        data: message,
      };
      this.emitter.emit('error', event);
      return;
    }

    // Handle API Gateway error responses (e.g. "Internal server error")
    if (typeof message.message === 'string' && message.connectionId) {
      logger.error('[Socket] Server error:', message.message, { connectionId: message.connectionId, requestId: message.requestId });
      const event: SocketEvent = {
        type: 'error',
        data: { message: message.message as string },
      };
      this.emitter.emit('error', event);
      return;
    }

    // Unknown message type
    if (isAreaEnabled('socket')) {
      logger.warn('[Socket] Unknown message type:', message);
    }
  }

  private isSubscriptionResponse(message: Record<string, unknown>): message is Record<string, unknown> & LabSubscriptionResponse {
    return message.subscribed !== undefined;
  }

  private isLabEvent(message: Record<string, unknown>): message is Record<string, unknown> & TypedLabEvent {
    const labEventTypes = [
      'ModelOperationProgress',
      'ModelOperationResult',
      'ModelPublished',
      'ModelVisibilityChange',
      'ModelCreated',
      'ModelDeleted',
      'ModelVersionCreated',
      'ModelVersionDeleted',
      'CheckpointCreated',
    ];
    return typeof message.type === 'string' && labEventTypes.includes(message.type);
  }

  private isLabPartnerStreamMessage(message: Record<string, unknown>): boolean {
    // Lab partner stream messages have specific structure
    const streamTypes = ['text_delta', 'model_action', 'feature_action', 'done', 'error', 'progress'];
    return typeof message.type === 'string' && streamTypes.includes(message.type) && !!message.requestId && !!message.sessionId;
  }

  async subscribeLab(labId: string): Promise<void> {
    if (isAreaEnabled('socket')) {
      logger.debug('[Socket] Subscribing to lab:', labId);
    }
    this.subscribedLabId = labId;

    const message: SocketMessage = {
      action: 'subscribe/lab',
      message: {
        labId,
      } as LabSubscriptionMessage,
    };

    const messageJson = JSON.stringify(message);
    const payloadSizeBytes = new Blob([messageJson]).size;
    logger.info(`[Socket] Outbound subscribe/lab | size=${(payloadSizeBytes / 1024).toFixed(1)} KB (${payloadSizeBytes} bytes) | labId=${labId}`);
    if (isAreaEnabled('socket')) {
      logger.debug('[Socket] Sending subscribe/lab message:', {
        action: message.action,
        labId: labId,
      });
    }

    if (this.status === 'connected' && this.socket?.readyState === WebSocket.OPEN) {
      try {
        this.socket.send(messageJson);
        if (isAreaEnabled('socket')) {
          logger.debug('[Socket] subscribe/lab message sent successfully');
        }
      } catch (error) {
        logger.error('[Socket] Failed to subscribe to lab:', error);
        // Will retry on reconnection via resubscribeToLab()
      }
    } else {
      if (isAreaEnabled('socket')) {
        logger.debug('[Socket] Not connected, will subscribe on connection');
      }
      // Will subscribe when connection is established via resubscribeToLab()
    }
  }

  unsubscribeLab(): void {
    if (isAreaEnabled('socket')) {
      logger.debug('[Socket] Unsubscribing from lab');
    }
    this.cancelSubscriptionRetry();
    this.subscribedLabId = null;
  }

  private async resubscribeToLab(): Promise<void> {
    if (!this.subscribedLabId) return;

    this.cancelSubscriptionRetry();
    this.lastSubscriptionConfirmedAt = null;

    const controller = new AbortController();
    this.subscriptionRetryController = controller;
    const { signal } = controller;
    const labId = this.subscribedLabId;
    const delays = [0, 5000, 10000, 15000]; // initial attempt + 3 retries

    for (let attempt = 0; attempt < delays.length; attempt++) {
      if (signal.aborted) return;

      // Wait before retry (skip for first attempt)
      if (delays[attempt] > 0) {
        try {
          await this.abortableSleep(delays[attempt], signal);
        } catch {
          return; // Aborted during sleep
        }
      }

      if (signal.aborted) return;

      // Bail if lab changed during wait
      if (this.subscribedLabId !== labId) return;

      // Bail if socket disconnected during wait
      if (this.status !== 'connected' || this.socket?.readyState !== WebSocket.OPEN) {
        logger.warn('Socket not connected during resubscription retry, aborting');
        return;
      }

      logger.info(`Re-subscribing to lab (attempt ${attempt + 1}/${delays.length}):`, labId);

      // Record when we sent the subscribe so we can detect late confirmations
      const subscribeSentAt = Date.now();

      // Send the subscribe message
      await this.subscribeLab(labId);

      // Wait for the server's confirmation
      try {
        await this.waitForSubscriptionConfirmation(signal);
        logger.info('Lab subscription confirmed for:', labId);
        this.subscriptionRetryController = null;
        return; // Success
      } catch (error) {
        if (signal.aborted) return;

        // Check if the confirmation actually arrived just after the timeout (late arrival)
        if (this.lastSubscriptionConfirmedAt && this.lastSubscriptionConfirmedAt > subscribeSentAt) {
          logger.info('Subscription confirmation arrived late but succeeded for:', labId);
          this.subscriptionRetryController = null;
          return; // Success — no retry needed
        }

        logger.warn(`Subscription confirmation failed (attempt ${attempt + 1}/${delays.length}):`, (error as Error).message);
      }
    }

    // All retries exhausted — force-close to trigger reconnection UI
    if (!signal.aborted) {
      this.subscriptionRetryController = null;
      this.forceCloseForSubscriptionFailure();
    }
  }

  private waitForSubscriptionConfirmation(signal: AbortSignal): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.cancelPendingSubscription();

      const timeout = setTimeout(() => {
        this.pendingSubscription = null;
        reject(new Error('Subscription confirmation timeout'));
      }, 10000);

      this.pendingSubscription = { resolve, reject, timeout };

      if (signal.aborted) {
        this.cancelPendingSubscription();
        reject(new Error('Subscription confirmation aborted'));
        return;
      }

      signal.addEventListener('abort', () => {
        this.cancelPendingSubscription();
        reject(new Error('Subscription confirmation aborted'));
      }, { once: true });
    });
  }

  private cancelPendingSubscription(): void {
    if (this.pendingSubscription) {
      clearTimeout(this.pendingSubscription.timeout);
      this.pendingSubscription = null;
    }
  }

  private cancelSubscriptionRetry(): void {
    if (this.subscriptionRetryController) {
      this.subscriptionRetryController.abort();
      this.subscriptionRetryController = null;
    }
    this.cancelPendingSubscription();
  }

  private abortableSleep(ms: number, signal: AbortSignal): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(resolve, ms);
      signal.addEventListener('abort', () => {
        clearTimeout(timeout);
        reject(new Error('Sleep aborted'));
      }, { once: true });
    });
  }

  private forceCloseForSubscriptionFailure(): void {
    logger.error('Lab subscription failed after retries, force-closing socket');
    if (this.socket) {
      this.socket.close(4001, 'Lab subscription failed');
    }
  }

  sendPing(): void {
    const message: SocketMessage = {
      action: 'ping',
      message: `Ping at ${new Date().toISOString()}`,
    };

    if (this.status === 'connected' && this.socket?.readyState === WebSocket.OPEN) {
      try {
        const pingJson = JSON.stringify(message);
        logger.debug(`[Socket] Outbound ping | size=${pingJson.length} bytes`);
        this.socket.send(pingJson);
      } catch (error) {
        logger.error(' Failed to send ping:', error);
      }
    }
  }


  private scheduleReconnect() {
    // Don't schedule if already reconnecting or max attempts reached
    if (this.isReconnecting || this.reconnectAttempts >= this.maxReconnectAttempts) {
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        logger.error('Max reconnection attempts reached');
        this.emitter.emit('error', {
          type: 'error',
          data: { message: 'Unable to establish connection after multiple attempts' },
        });
        // Open circuit breaker to prevent excessive retries
        this.isCircuitOpen = true;
        this.circuitBreakerResetTime = Date.now() + this.circuitBreakerTimeout;
      }
      return;
    }

    // Don't schedule if a connection is already in progress
    if (this.connectionPromise) {
      logger.debug('Connection already in progress, skipping reconnect schedule');
      return;
    }

    // Don't schedule if disconnecting
    if (this.isDisconnecting) {
      logger.debug('Disconnecting in progress, skipping reconnect schedule');
      return;
    }

    // Don't schedule if circuit breaker is open
    if (this.isCircuitOpen) {
      logger.debug('Circuit breaker open, skipping reconnect schedule');
      return;
    }

    this.isReconnecting = true;
    this.reconnectAttempts++;

    // Calculate exponential backoff with jitter
    const baseDelay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    const maxDelay = Math.min(baseDelay, 30000); // Cap at 30 seconds

    // Add jitter: random value between 0% and 30% of the delay
    const jitter = Math.random() * maxDelay * 0.3;
    const delay = Math.floor(maxDelay + jitter);

    logger.info(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      this.isReconnecting = false; // Reset flag before attempting connection
      this.connect();
    }, delay);
  }

  private setStatus(status: ConnectionStatus) {
    if (this.status !== status) {
      logger.debug(`Status changed: ${this.status} → ${status}`);
      this.status = status;

      const event: SocketEvent = {
        type: 'connection_status',
        data: { status },
      };

      this.emitter.emit('status', event);
    }
  }

  private startKeepAlive() {
    this.stopKeepAlive();

    this.keepAliveInterval = setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        // Send ping
        this.sendPing();

        // Set timeout for pong response
        this.pingTimeout = setTimeout(() => {
          this.missedPongs++;
          logger.warn(`[Socket] Pong timeout! Missed pongs: ${this.missedPongs}`);

          if (this.missedPongs >= this.MAX_MISSED_PONGS) {
            logger.error('[Socket] Connection appears dead, forcing reconnect');
            // Force close the socket to trigger reconnection
            if (this.socket) {
              this.socket.close(4000, 'Ping timeout');
            }
            this.setStatus('error');
            this.scheduleReconnect();
          }
        }, this.PONG_TIMEOUT);
      }
    }, this.PING_INTERVAL);
  }

  private stopKeepAlive() {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = null;
    }

    // Also clear ping timeout
    if (this.pingTimeout) {
      clearTimeout(this.pingTimeout);
      this.pingTimeout = null;
    }
  }

  disconnect() {
    logger.info('🔌 Disconnecting');

    // Set disconnecting flag to prevent concurrent connections
    this.isDisconnecting = true;

    try {
      // Stop all timers and intervals
      this.stopKeepAlive();
      this.cancelSubscriptionRetry();

      // Clear reconnection timeout if scheduled
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }

      this.isReconnecting = false;
      this.reconnectAttempts = this.maxReconnectAttempts; // Prevent auto-reconnect

      // Close WebSocket connection
      if (this.socket) {
        this.detachSocketHandlers();
        this.socket.close(1000, 'User disconnect');
        this.socket = null;
      }

      // Clear connection promise (if any connection attempt is still waiting)
      this.connectionPromise = null;

      // Clear state
      this.setStatus('disconnected');
      this.subscribedLabId = null;

      // Clean up network and visibility listeners
      this.cleanupNetworkMonitoring();
      this.cleanupVisibilityMonitoring();
    } finally {
      // Always reset disconnecting flag
      this.isDisconnecting = false;
    }
  }

  private setupNetworkMonitoring() {
    if (typeof window === 'undefined') return;

    // Initial state
    this.isOnline = navigator.onLine;

    // Handle coming back online
    this.networkListener = () => {
      logger.info('📶 Network connection restored');
      this.isOnline = true;

      // Only reconnect if we were previously disconnected due to network
      if (this.status === 'disconnected' || this.status === 'error') {
        logger.info('🔄 Attempting to reconnect after network restoration');
        this.reconnect();
      }
    };

    // Handle going offline
    this.offlineListener = () => {
      logger.warn('📵 Network connection lost');
      this.isOnline = false;

      // Close existing connection
      if (this.socket) {
        this.detachSocketHandlers();
        this.socket.close(1000, 'Network offline');
        this.socket = null;
      }

      this.setStatus('disconnected');
      this.stopKeepAlive();
    };

    window.addEventListener('online', this.networkListener);
    window.addEventListener('offline', this.offlineListener);
  }

  private cleanupNetworkMonitoring() {
    if (typeof window === 'undefined') return;

    if (this.networkListener) {
      window.removeEventListener('online', this.networkListener);
      this.networkListener = null;
    }

    if (this.offlineListener) {
      window.removeEventListener('offline', this.offlineListener);
      this.offlineListener = null;
    }
  }

  private setupVisibilityMonitoring() {
    if (typeof document === 'undefined') return;

    this.visibilityListener = () => {
      const isVisible = document.visibilityState === 'visible';

      if (isVisible && !this.isPageVisible) {
        // Page became visible
        logger.debug('👁️ Page became visible');
        this.isPageVisible = true;

        // Reconnect if we were disconnected or in error state while hidden
        if ((this.status === 'disconnected' || this.status === 'error') && this.isOnline) {
          logger.info('🔄 Reconnecting after page became visible');
          this.reconnect();
        }
      } else if (!isVisible && this.isPageVisible) {
        // Page became hidden
        logger.debug('🙈 Page became hidden');
        this.isPageVisible = false;

        // Keep connection alive when page is hidden to prevent message loss
        // The ping/pong mechanism will detect if the connection dies
        logger.debug('🔌 Maintaining connection while page is hidden to prevent message loss');
      }
    };

    document.addEventListener('visibilitychange', this.visibilityListener);
  }

  private cleanupVisibilityMonitoring() {
    if (typeof document === 'undefined') return;

    if (this.visibilityListener) {
      document.removeEventListener('visibilitychange', this.visibilityListener);
      this.visibilityListener = null;
    }
  }

  // ============================================
  // Lab Partner Methods
  // ============================================

  /**
   * Register a lab partner session
   */
  async registerLabPartnerSession(sessionId: string): Promise<void> {
    if (isAreaEnabled('socket')) {
      logger.debug('[Socket] Registering lab partner session:', sessionId);
    }

    const message: SocketMessage = {
      action: 'lab-partner/register',
      message: {
        sessionId,
      } as LabPartnerRegisterMessage,
    };

    const messageJson = JSON.stringify(message);
    const payloadSizeBytes = new Blob([messageJson]).size;
    logger.info(`[Socket] Outbound lab-partner/register | size=${(payloadSizeBytes / 1024).toFixed(1)} KB (${payloadSizeBytes} bytes) | sessionId=${sessionId}`);

    if (this.status === 'connected' && this.socket?.readyState === WebSocket.OPEN) {
      try {
        this.socket.send(messageJson);
        if (isAreaEnabled('socket')) {
          logger.debug('[Socket] lab-partner/register message sent successfully');
        }
      } catch (error) {
        logger.error('[Socket] Failed to register lab partner session:', error);
        throw error;
      }
    } else {
      logger.warn('[Socket] Cannot register lab partner session - not connected');
      throw new Error('WebSocket not connected');
    }
  }

  /**
   * Send a lab partner ask message
   */
  async sendLabPartnerAsk(askMessage: LabPartnerAskMessage): Promise<{ payloadSizeBytes: number }> {
    if (isAreaEnabled('socket')) {
      logger.debug('[Socket] Sending lab partner ask message:', {
        sessionId: askMessage.sessionId,
        requestId: askMessage.requestId,
        prompt: askMessage.prompt.substring(0, 100) + '...',
      });
    }

    const message: SocketMessage = {
      action: 'lab-partner/ask',
      message: askMessage,
    };

    const messageJson = JSON.stringify(message);
    const payloadSizeBytes = new Blob([messageJson]).size;
    const payloadSizeKB = (payloadSizeBytes / 1024).toFixed(1);
    logger.info(`[Socket] Outbound lab-partner/ask | size=${payloadSizeKB} KB (${payloadSizeBytes} bytes) | requestId=${askMessage.requestId} | sessionId=${askMessage.sessionId}`);


    if (this.status === 'connected' && this.socket?.readyState === WebSocket.OPEN) {
      try {
        this.socket.send(messageJson);
        if (isAreaEnabled('socket')) {
          logger.debug('[Socket] lab-partner/ask message sent successfully');
        }
        return { payloadSizeBytes };
      } catch (error) {
        logger.error('[Socket] Failed to send lab partner ask message:', error);
        throw error;
      }
    } else {
      logger.warn('[Socket] Cannot send lab partner message - not connected', {
        status: this.status,
        socketReadyState: this.socket?.readyState
      });
      throw new Error('WebSocket not connected');
    }
  }

  getStatus(): ConnectionStatus {
    return this.status;
  }

  isConnected(): boolean {
    return this.status === 'connected';
  }

  getPingHealth() {
    return {
      lastPongReceived: this.lastPongReceived,
      missedPongs: this.missedPongs,
      isHealthy: this.missedPongs < this.MAX_MISSED_PONGS,
    };
  }

  getReconnectState() {
    return {
      isCircuitOpen: this.isCircuitOpen,
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts,
      hasExhaustedRetries: this.reconnectAttempts >= this.maxReconnectAttempts || this.isCircuitOpen,
    };
  }

  // Event emitter methods
  on(event: keyof Events, handler: (data: SocketEvent) => void) {
    this.emitter.on(event, handler);
  }

  off(event: keyof Events, handler: (data: SocketEvent) => void) {
    this.emitter.off(event, handler);
  }
}

export const socketConnection = SocketConnection.getInstance();